const EMERGENCY_API_BASE = "https://alerta-maceio-api-production.up.railway.app/api";

const QUICK_DIAL = [
  { key: "qdDanger", number: "190", icon: "siren", tone: "alert" },
  { key: "qdMedical", number: "192", icon: "heart-pulse", tone: "brand" },
  { key: "qdFire", number: "193", icon: "flame", tone: "warning" },
  { key: "qdWomen", number: "180", icon: "shield-check", tone: "neutral" },
  { key: "qdAnonymous", number: "181", icon: "megaphone", tone: "neutral", full: true },
];

const GROUP_ORDER = ["Batalhões e unidades da PM", "UPAs", "Hospitais", "Delegacias e unidades especializadas"];
const GROUP_ICON = {
  "Batalhões e unidades da PM": "shield-check",
  "UPAs": "building-2",
  "Hospitais": "building-2",
  "Delegacias e unidades especializadas": "building-2",
};
const GROUP_KEY = {
  "Batalhões e unidades da PM": "groupPM",
  "UPAs": "groupUPA",
  "Hospitais": "groupHospital",
  "Delegacias e unidades especializadas": "groupDelegacia",
};

function UnitMap({ lang, lat, lng }) {
  const mapDivRef = React.useRef(null);

  React.useEffect(() => {
    if (lat == null || lng == null || !mapDivRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true, touchZoom: true, attributionControl: false }).setView([lat, lng], 16);
    L.tileLayer(window.VIGI_TILE_URL, window.VIGI_TILE_OPTIONS).addTo(map);
    const icon = L.divIcon({ className: "", html: '<div class="vigi-map-pin" style="background:var(--color-alert)"></div>', iconSize: [13, 13], iconAnchor: [7, 7] });
    L.marker([lat, lng], { icon }).addTo(map);
    return () => { map.remove(); };
  }, [lat, lng]);

  if (lat == null || lng == null) {
    return <div style={{ padding: "8px 0", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>{t(lang, "noCoordinates")}</div>;
  }
  return <div ref={mapDivRef} style={{ height: 130, borderRadius: 9, overflow: "hidden", position: "relative", zIndex: 0, border: "1px solid var(--color-border)" }} />;
}

function UnitCard({ lang, unit, open, onToggle }) {
  const { Icon } = window.VigiALDesignSystem_ff2dec;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(unit.addr)}`;
  const iconName = GROUP_ICON[unit.group] || "building-2";
  return (
    <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", overflow: "hidden", fontFamily: "var(--font-body)" }}>
      <button onClick={onToggle} style={{ width: "100%", background: "transparent", border: "none", padding: "9px 10px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Icon name={iconName} size={16} color="var(--color-text-secondary)" />
          <span style={{ fontSize: "var(--text-base)", textAlign: "left", color: "var(--color-text-primary)" }}>{unit.name}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {unit.distanceKm != null && (
            <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", fontFamily: "var(--font-mono)" }}>{unit.distanceKm} km</span>
          )}
          <span style={{ color: "var(--color-text-muted)", transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
        </div>
      </button>
      {open && (
        <div style={{ padding: "0 10px 10px 34px", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>
          <div style={{ marginBottom: 6 }}>{unit.addr}</div>
          {unit.phone && <div style={{ marginBottom: 8, fontFamily: "var(--font-mono)" }}>{unit.phone}</div>}
          <UnitMap lang={lang} lat={unit.lat} lng={unit.lng} />
          <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--color-brand-primary)", fontSize: "var(--text-sm)", marginTop: 8 }}>
            {t(lang, "openRouteGoogleMaps")}
          </a>
        </div>
      )}
    </div>
  );
}

function EmergenciaScreen({ lang, expanded, setExpanded }) {
  const { QuickDialCard, Icon } = window.VigiALDesignSystem_ff2dec;
  const IconFor = (name) => (p) => <Icon name={name} {...p} />;
  const [units, setUnits] = React.useState([]);
  const [locStatus, setLocStatus] = React.useState("loading");

  React.useEffect(() => {
    function fetchUnits(lat, lng) {
      const qs = (lat != null && lng != null) ? `?lat=${lat}&lng=${lng}` : "";
      fetch(`${EMERGENCY_API_BASE}/units${qs}`)
        .then((r) => r.json())
        .then(setUnits)
        .catch(() => {});
    }
    if (!navigator.geolocation) { setLocStatus("denied"); fetchUnits(); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocStatus("ok"); fetchUnits(pos.coords.latitude, pos.coords.longitude); },
      () => { setLocStatus("denied"); fetchUnits(); },
      { timeout: 3000 }
    );
  }, []);

  const groupedUnits = GROUP_ORDER.map((label) => ({
    label,
    items: units.filter((u) => u.group === label),
  })).filter((g) => g.items.length > 0);

  return (
    <div style={{ padding: 14, flex: 1, overflowY: "auto" }}>
      <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: 8 }}>{t(lang, "whatsHappening")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
        {QUICK_DIAL.map((q) => (
          <div key={q.number} style={q.full ? { gridColumn: "1 / -1" } : undefined}>
            <QuickDialCard label={t(lang, q.key)} number={q.number} tone={q.tone} icon={IconFor(q.icon)} />
          </div>
        ))}
      </div>
      {locStatus === "ok" && (
        <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 10 }}>
          {t(lang, "sortedByLocation")}
        </div>
      )}
      {groupedUnits.map((g) => (
        <div key={g.label} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", margin: "6px 0" }}>{t(lang, GROUP_KEY[g.label])}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {g.items.map((u) => (
              <UnitCard key={u.id} lang={lang} unit={u}
                open={expanded === u.id} onToggle={() => setExpanded(expanded === u.id ? null : u.id)} />
            ))}
          </div>
        </div>
      ))}
      <div style={{ marginTop: 4, background: "var(--color-warning-tint)", borderRadius: "var(--radius-lg)", padding: 10, display: "flex", gap: 6, fontSize: "var(--text-xs)", color: "#7A5410" }}>
        <Icon name="info" size={14} color="#7A5410" style={{ flexShrink: 0, marginTop: 1 }} />
        {t(lang, "emergencyDisclaimer")}
      </div>
    </div>
  );
}
window.EmergenciaScreen = EmergenciaScreen;
